import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, GraduationCap, Loader2, Send, Settings, Wrench } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import api, { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import { Reveal } from "@/components/Reveal";

const inputCls = "w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none";

const SERVICES = [
    { key: "install", icon: Settings, title: "Install & setup", price: "from $350", desc: "We uncrate it, level it, prime the lines, profile the ink and run your first good print with you." },
    { key: "repair", icon: Wrench, title: "Repair call-out", price: "from $150 + parts", desc: "Clogged heads, dead dampers, belt and encoder issues, powder shakers that stopped shaking." },
    { key: "maintenance", icon: CheckCircle2, title: "Maintenance plan", price: "from $99/visit", desc: "Scheduled cleaning, capping station and wiper service, white ink circulation check." },
    { key: "training", icon: GraduationCap, title: "Operator training", price: "from $250", desc: "Two hours on your own machine: film, powder, cure settings, color and wash testing." },
];

export default function MachineServices() {
    const [form, setForm] = useState({ type: "install", machine: "", name: "", email: "", phone: "", location: "", details: "", preferred_date: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async () => {
        if (!form.name || !form.email || !form.phone) { toast.error("Name, email and phone are required"); return; }
        setBusy(true);
        try {
            await api.post("/marketplace/services/requests", { ...form, preferred_date: form.preferred_date || null });
            setSent(true);
            toast.success("Request sent — we'll text you to schedule");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <Layout>
            <PageIntro trail={[{ label: "Machine service" }]} kicker="Machine services · Brooklyn" title="Install, repair & training." testid="services-intro"
                description="Bought a DTF or DTG machine — here or anywhere else? We run this gear every day in Brooklyn. Pick the kind of visit below, tell us about the machine, and we text you to schedule." />

            <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="service-cards">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.key} delay={i * 0.05}>
                            <button onClick={() => setForm((f) => ({ ...f, type: s.key }))} data-testid={`service-card-${s.key}`}
                                className={`h-full w-full rounded-3xl border bg-white p-6 text-left transition-all duration-300 hover:-translate-y-1 ${form.type === s.key ? "border-zinc-900 ring-2 ring-zinc-900" : "border-zinc-200 hover:border-zinc-400"}`}>
                                <s.icon size={22} className="text-zinc-900" />
                                <h3 className="mt-3 font-display text-lg font-bold text-zinc-900">{s.title}</h3>
                                <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{s.price}</p>
                                <p className="mt-3 text-sm text-zinc-600">{s.desc}</p>
                            </button>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-10 grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        {sent ? (
                            <div className="rounded-3xl border border-emerald-300 bg-emerald-50 p-8" data-testid="service-sent">
                                <h2 className="font-display text-2xl font-black text-zinc-900">Request received</h2>
                                <p className="mt-2 text-zinc-700">We'll text you at {form.phone} to lock in a time. Urgent? Call (347) 573-3875.</p>
                            </div>
                        ) : (
                            <div className="space-y-4 rounded-3xl border border-zinc-200 bg-white p-6" data-testid="service-form">
                                <h2 className="font-display text-xl font-bold text-zinc-900">Book a {SERVICES.find((s) => s.key === form.type)?.title.toLowerCase()}</h2>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <input className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} data-testid="service-name" />
                                    <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="service-phone" />
                                    <input className={`${inputCls} sm:col-span-2`} placeholder="Email" value={form.email} onChange={set("email")} data-testid="service-email" />
                                    <input className={inputCls} placeholder="Machine — e.g. A2 dual i1600" value={form.machine} onChange={set("machine")} data-testid="service-machine" />
                                    <input className={inputCls} placeholder="Where is it? (city / zip)" value={form.location} onChange={set("location")} data-testid="service-location" />
                                    <input className={`${inputCls} sm:col-span-2`} type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="service-date" />
                                    <textarea className={`${inputCls} sm:col-span-2`} rows={4} placeholder="What's going on with it?" value={form.details} onChange={set("details")} data-testid="service-details" />
                                </div>
                                <GreenButton className="w-full" onClick={submit} disabled={busy} data-testid="service-submit">
                                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send request
                                </GreenButton>
                            </div>
                        )}
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-6">
                            <h3 className="font-display text-lg font-bold text-zinc-900">Why us</h3>
                            <ul className="mt-3 space-y-2 text-sm text-zinc-600">
                                <li>· Printing in Brooklyn since 2012 — we fix our own machines daily.</li>
                                <li>· We stock the film, powder and ink, so we can test with real consumables.</li>
                                <li>· Five boroughs, Long Island and North Jersey on site; remote diagnosis anywhere.</li>
                                <li>· Buy a machine through our marketplace and install is discounted.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
